import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { useServices } from "@/lib/openapi";
import type {
    MemoryEntrySchema,
    PersonalContextExportSchema,
    WipeReceiptSchema,
} from "@/lib/openapi/gen/types.gen";

export type {
    MemoryEntrySchema,
    PersonalContextExportSchema,
    WipeReceiptSchema,
};

/**
 * What the platform has learned about you, and the controls over it.
 *
 * Every endpoint behind this store is scoped to the authenticated user on the
 * server — none of them takes a user id — so there is nothing to pass and
 * nothing to get wrong here.
 */
export const usePersonalMemoryStore = defineStore("personal-memory", () => {
    const entries = ref<MemoryEntrySchema[]>([]);
    const memoryEnabled = ref(true);
    const probeEnabled = ref(true);
    const settingsLoaded = ref(false);
    const isLoading = ref(false);
    const isWiping = ref(false);
    const error = ref<string>();

    /** Profile sections, always shown in full. */
    const fixedSlots = computed(() =>
        entries.value.filter((entry) => entry.slot_kind === "fixed")
    );

    /** Everything the assistant has written down about your work. */
    const freeEntries = computed(() =>
        entries.value.filter((entry) => entry.slot_kind !== "fixed")
    );

    const isEmpty = computed(() => entries.value.length === 0);

    // Personal context spans organisations, so no org/stek header is required.
    const api = useServices({
        opts: { throwOnError: false },
        requires: {
            auth: true,
            organization: false,
            stek: false,
        },
    });

    /**
     * Load the *stored* switch positions. The toggles must show state, not a
     * client-side default — for a privacy control, a guessed position is a lie.
     */
    const fetchSettings = async () => {
        try {
            const { response, data } =
                await api.getPersonalMemorySettingsMePersonalMemorySettingsGet(
                    {}
                );
            if (!response?.ok || !data) {
                return;
            }
            memoryEnabled.value = data.personal_memory_enabled;
            probeEnabled.value = data.attention_probe_enabled;
            settingsLoaded.value = true;
        } catch (err) {
            console.error("Failed to load personal-memory settings:", err);
        }
    };

    const fetchEntries = async () => {
        isLoading.value = true;
        error.value = undefined;
        try {
            const { response, data } = await api.listMemoryMeMemoryGet({});
            if (!response?.ok) {
                error.value = "Could not load what is remembered about you.";
                return;
            }
            entries.value = data ?? [];
        } catch (err) {
            console.error("Failed to load personal memory:", err);
            error.value = "Could not load what is remembered about you.";
        } finally {
            isLoading.value = false;
        }
    };

    const forgetEntry = async (entryId: string) => {
        error.value = undefined;
        try {
            const { response } =
                await api.deleteMemoryEntryMeMemoryEntryIdDelete({
                    path: { entry_id: entryId },
                });
            if (!response?.ok) {
                error.value = "Could not forget that entry.";
                return false;
            }
            // Drop it locally rather than refetching — the server has already
            // agreed, and a refetch would make the row linger visibly.
            entries.value = entries.value.filter(
                (entry) => entry.identity !== entryId
            );
            return true;
        } catch (err) {
            console.error("Failed to delete a memory entry:", err);
            error.value = "Could not forget that entry.";
            return false;
        }
    };

    const setEnabled = async (enabled: boolean) => {
        error.value = undefined;
        try {
            const { response, data } =
                await api.setPersonalMemoryEnabledMePersonalMemoryEnabledPut({
                    body: { enabled },
                });
            if (!response?.ok) {
                error.value = "Could not change that setting.";
                return false;
            }
            memoryEnabled.value = data?.enabled ?? enabled;
            return true;
        } catch (err) {
            console.error("Failed to set the personal-memory switch:", err);
            error.value = "Could not change that setting.";
            return false;
        }
    };

    const setProbeEnabled = async (enabled: boolean) => {
        error.value = undefined;
        try {
            const { response, data } =
                await api.setAttentionProbeEnabledMeAttentionProbeEnabledPut({
                    body: { enabled },
                });
            if (!response?.ok) {
                error.value = "Could not change that setting.";
                return false;
            }
            probeEnabled.value = data?.enabled ?? enabled;
            return true;
        } catch (err) {
            console.error("Failed to set the attention-probe switch:", err);
            error.value = "Could not change that setting.";
            return false;
        }
    };

    const wipeAll = async (): Promise<WipeReceiptSchema | undefined> => {
        isWiping.value = true;
        error.value = undefined;
        try {
            const { response, data } =
                await api.wipeContextMePersonalContextDelete({});
            if (!response?.ok || !data) {
                error.value = "Could not erase your personal context.";
                return undefined;
            }
            entries.value = [];
            if (!data.complete) {
                // The durable half is gone; say so rather than showing a clean
                // success for an erasure that did not finish.
                error.value =
                    "Most of your data was erased, but one store could not be " +
                    "reached. It will expire on its own shortly — try again if " +
                    "you would rather not wait.";
            }
            return data;
        } catch (err) {
            console.error("Failed to wipe personal context:", err);
            error.value = "Could not erase your personal context.";
            return undefined;
        } finally {
            isWiping.value = false;
        }
    };

    const downloadExport = async () => {
        error.value = undefined;
        try {
            const { response, data } =
                await api.exportContextMePersonalContextExportGet({});
            if (!response?.ok || !data) {
                error.value = "Could not build your export.";
                return;
            }
            triggerDownload(data);
        } catch (err) {
            console.error("Failed to export personal context:", err);
            error.value = "Could not build your export.";
        }
    };

    return {
        entries,
        fixedSlots,
        freeEntries,
        isEmpty,
        memoryEnabled,
        probeEnabled,
        settingsLoaded,
        isLoading,
        isWiping,
        error,
        fetchSettings,
        fetchEntries,
        forgetEntry,
        setEnabled,
        setProbeEnabled,
        wipeAll,
        downloadExport,
    };
});

/** Hand the payload to the browser as a file. Extracted so it can be stubbed. */
export function triggerDownload(payload: PersonalContextExportSchema) {
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "mystekz-personal-context.json";
    link.click();
    URL.revokeObjectURL(url);
}
